// Shared protocol constants — the single source of truth for timeouts, wire
// limits, WebSocket close codes, and signature domain-separation prefixes.
// Consumed by both the TypeScript clients and (via the JSON exports) the Go
// server, so these numbers must never be duplicated with local literals.

/**
 * Timeouts and budgets from docs/02-signaling-and-api.md §4.
 * All values are milliseconds unless the name says otherwise.
 */
export const TIMEOUTS = {
  /** Unanswered `call.invite` expires after this → `call.end {reason:"timeout"}`. */
  RING_MS: 45_000,
  /** Total budget to recover a flapped connection before the call is dropped. */
  RECONNECT_BUDGET_MS: 60_000,
  /** WS heartbeat interval; two consecutive misses ⇒ reconnect. */
  HEARTBEAT_MS: 25_000,
  /** Per-connection server→client replay buffer horizon for resume. */
  REPLAY_BUFFER_MS: 5 * 60_000,
  /** Client must send its first frame (`sys.auth`) within this of the socket opening. */
  AUTH_DEADLINE_MS: 5_000,
  /** RTP inactivity teardown: no media for this long + failed ICE restart ⇒ end. */
  RTP_TIMEOUT_MS: 30_000,
  /** Initial ICE establishment deadline after answer (docs/02 §4 state chart). */
  ICE_CONNECT_MS: 20_000,
} as const;

/** Maximum size of a single WS frame, in bytes (docs/02 §4). */
export const MAX_FRAME_BYTES = 64 * 1024;

/**
 * WebSocket close codes (application range 4000–4999). `4001` and `4401` are
 * named in docs/02 §4; the rest are defined here so the Go server and the
 * clients agree on one authoritative set (this package is the source of truth).
 */
export const WS_CLOSE = {
  /** Malformed frame, or any non-`sys.auth` frame sent before auth completes. */
  BAD_FRAME: 4400,
  /** Session was terminated (revoked `sid`); sent with `sys.revoked` when possible. */
  SESSION_REVOKED: 4001,
  /** Client did not send `sys.auth` within `AUTH_DEADLINE_MS`. */
  AUTH_TIMEOUT: 4401,
  /** `sys.auth` token was invalid, expired, or its `sid` is revoked. */
  AUTH_FAILED: 4403,
  /** Resume requested an unknown or expired `connId`; client must re-auth fresh. */
  RESUME_FAILED: 4409,
  /**
   * A lower-priority agent tried to take a session that a live in-app socket
   * already holds. The refused socket must STAND DOWN — back off and stay off
   * while the app owns the session — rather than treat this as a lost
   * connection and retry, which is what produced a connect/evict ping-pong
   * between the Android native reach listener and the WebView's own socket.
   * Only ever sent to an `agent:"reach"` connection.
   */
  STAND_DOWN: 4410,
  /** Frame exceeded `MAX_FRAME_BYTES`. */
  FRAME_TOO_LARGE: 4413,
} as const;

export type WsCloseCode = (typeof WS_CLOSE)[keyof typeof WS_CLOSE];

/**
 * Ed25519 signature domain-separation prefixes. Every signature is computed
 * over `<prefix> || <context>` so a signature minted for one purpose can never
 * be replayed for another (docs/03-security.md; enumerated in the J1 card).
 */
export const SIG_DOMAIN = {
  /** Challenge-response login: signs the server nonce. */
  AUTH: 'merkury-auth-v1',
  /** E2EE integrity: signs the DTLS fingerprint carried in call SDP. */
  SDP: 'merkury-sdp-v1',
  /** Account deletion: fresh identity-key signature guarding `DELETE /me`. */
  DELETE: 'merkury-delete-v1',
  /** Password auth key derivation branch (client-side split-KDF). */
  PW_AUTH: 'merkury-pw-auth-v1',
  /** Password encryption key derivation branch (client-side split-KDF). */
  PW_ENC: 'merkury-pw-enc-v1',
  /**
   * Passkey identity-restore: HKDF-SHA256 `info` that turns a WebAuthn PRF
   * output (a per-credential secret the server never sees, docs/03 §2) into the
   * key that wraps the identity seed. Domain-separates the PRF bytes from any
   * other use of the same output.
   */
  PASSKEY_PRF: 'merkury-passkey-prf-v1',
  /** Endpoint-list document: signs canonical JSON of the list sans `signature`. */
  ENDPOINTS: 'merkury-endpoints-v1',
  /**
   * Contact-alias blob encryption: HKDF-SHA256 `info` that derives, from the
   * identity seed, the XChaCha20-Poly1305 key wrapping the alias map synced
   * via `/me/aliases` (docs/08 §8). The server stores only the ciphertext;
   * domain-separates the seed bytes from every other derivation.
   */
  ALIAS_ENC: 'merkury-alias-enc-v1',
} as const;

export type SigDomain = (typeof SIG_DOMAIN)[keyof typeof SIG_DOMAIN];
